import React, { useEffect, useState, useMemo } from "react";
import Card from "react-bootstrap/Card";
import Modal from "react-bootstrap/Modal";
import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import Badge from "react-bootstrap/Badge";
import { FaArrowTrendUp, FaArrowTrendDown } from "react-icons/fa6";
import axios from "axios";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import Dettagli from "./DettagliOrdini";

// ── Colori coerenti con lo stile bootstrap del resto della dashboard ──
const COLOR_STOCK = "#495057"; // grigio scuro - stock "puro"
const COLOR_CROSS = "#0d6efd"; // blu bootstrap - cross docking
const COLOR_NOSTOCK = "#adb5bd"; // grigio chiaro - no stock

// ── Un articolo è "Cross Docking" se stock === "stock" e note_agg inizia per FF (LIKE 'FF%') ──
const isCrossDocking = (item) =>
  typeof item.note_agg === "string" &&
  item.note_agg.trim().toUpperCase().startsWith("FF");

const categorizeStockItem = (item) => {
  if (isCrossDocking(item)) return "Cross Docking";
  if (item.stock === "stock") return "Stock";
  if (item.stock === "no stock") return "No Stock";
  return "Altro";
};

const formatToEuro = (amount) =>
  new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount || 0);

const formatDataIt = (isoDay) => {
  if (!isoDay) return "";
  const d = new Date(isoDay);
  if (Number.isNaN(d.getTime())) return isoDay;
  return d.toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

// ────────────────────────────────────────────────────────────────
// Modale Stock: andamento giornaliero + tabella SOLO dettagli Stock
// ────────────────────────────────────────────────────────────────
function StockModal({ show, onHide, stockItems }) {
  // 1. Arricchiamo i dati e FILTRIAMO SOLO quelli che appartengono alla categoria "Stock"
  const stockPuri = useMemo(() => {
    return stockItems
      .map((item) => ({
        ...item,
        categoria: categorizeStockItem(item),
        giorno: item.data_ordine?.split(" ")[0] || null,
        valore: parseFloat(item.ordinato || 0),
      }))
      .filter((item) => item.categoria === "Stock");
  }, [stockItems]);

  // Totalizzatore solo per lo Stock
  const totaleStockValore = useMemo(() => {
    return stockPuri.reduce((acc, item) => acc + item.valore, 0);
  }, [stockPuri]);

  // ── Aggregazione per giorno per il grafico (solo Stock) ──
  const datiGiornalieri = useMemo(() => {
    const mappa = {};
    stockPuri.forEach((item) => {
      if (!item.giorno) return;
      if (!mappa[item.giorno]) {
        mappa[item.giorno] = {
          giorno: item.giorno,
          giornoLabel: formatDataIt(item.giorno),
          Stock: 0,
        };
      }
      mappa[item.giorno].Stock += item.valore;
    });
    return Object.values(mappa).sort(
      (a, b) => new Date(a.giorno) - new Date(b.giorno),
    );
  }, [stockPuri]);

  // ── Righe tabella, più recenti in alto ──
  const righeTabella = useMemo(
    () =>
      [...stockPuri].sort(
        (a, b) => new Date(b.data_ordine) - new Date(a.data_ordine),
      ),
    [stockPuri],
  );

  const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="p-2 shadow-sm rounded border bg-white"
        style={{ fontSize: "0.8rem" }}
      >
        <p className="fw-bold mb-1 text-muted">{label}</p>
        <p className="mb-0 text-dark fw-bold">
          Valore:{" "}
          <span className="text-primary">
            {new Intl.NumberFormat("it-IT", {
              style: "currency",
              currency: "EUR",
            }).format(payload[0].value)}
          </span>
        </p>
      </div>
    );
  }
  return null;
};

  return (
<Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      scrollable
      contentClassName="border-0 shadow-lg"
      style={{ borderRadius: "16px" }}
    >
      {/* ── HEADER ── */}
      <Modal.Header closeButton className="border-0 pb-0 pt-4 px-4">
        <Modal.Title className="fw-bold d-flex align-items-center gap-2">
          <span>Dettaglio Articoli Stock</span>
          <Badge
            bg="dark"
            pill
            className="fw-normal fs-6 px-3 py-1"
            style={{ opacity: 0.85 }}
          >
            {righeTabella.length} {righeTabella.length === 1 ? "articolo" : "articoli"}
          </Badge>
        </Modal.Title>
      </Modal.Header>

      {/* ── BODY ── */}
      <Modal.Body className="p-4">
        {/* 1. Stat Card Top */}
        <div className="row mb-4">
          <div className="col-md-5 col-lg-4">
            <div
              className="p-3 rounded-3 shadow-sm border-0 d-flex flex-column justify-content-between"
              style={{
                background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                borderLeft: `5px solid ${COLOR_STOCK}`,
              }}
            >
              <span
                className="text-uppercase fw-bold text-muted tracking-wide"
                style={{ fontSize: "0.7rem", letterSpacing: "0.5px" }}
              >
                Totale Valore Stock
              </span>
              <div className="fw-bold fs-3 text-dark mt-1">
                {formatToEuro(totaleStockValore)}
              </div>
            </div>
          </div>
        </div>

        {/* 2. Sezione Grafico */}
        <div className="bg-light p-3 rounded-3 border border-light mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6
              className="fw-bold text-uppercase mb-0 text-secondary"
              style={{ fontSize: "0.75rem", letterSpacing: "0.5px" }}
            >
              Andamento Giornaliero
            </h6>
          </div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <ComposedChart
                data={datiGiornalieri}
                margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" vertical={false} />
                <XAxis
                  dataKey="giornoLabel"
                  fontSize={11}
                  tickLine={false}
                  axisLine={{ stroke: "#ccc" }}
                />
                <YAxis
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) =>
                    new Intl.NumberFormat("it-IT", {
                      notation: "compact",
                      compactDisplay: "short",
                    }).format(v)
                  }
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  wrapperStyle={{ fontSize: "0.75rem", paddingTop: "8px" }}
                />
                <Bar
                  dataKey="Stock"
                  name="Valore Stock"
                  fill={COLOR_STOCK}
                  radius={[6, 6, 0, 0]}
                  barSize={28}
                />
                <Line
                  type="monotone"
                  dataKey="Stock"
                  name="Trend"
                  stroke="#212529"
                  strokeWidth={2}
                  dot={{ r: 4, fill: "#212529" }}
                  activeDot={{ r: 6 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* 3. Sezione Tabella */}
        <div>
          <h6
            className="fw-bold text-uppercase mb-3 text-secondary"
            style={{ fontSize: "0.75rem", letterSpacing: "0.5px" }}
          >
            Elenco Dettagliato
          </h6>
          <div
            className="border rounded-3 overflow-hidden shadow-sm"
            style={{ maxHeight: 340, overflowY: "auto" }}
          >
            <Table hover responsive className="align-middle mb-0" style={{ fontSize: "0.85rem" }}>
              <thead
                className="table-dark"
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                }}
              >
                <tr>
                  <th className="py-2 px-3">Data</th>
                  <th className="py-2">Categoria</th>
                  <th className="py-2">Articolo</th>
                  <th className="py-2">Ragione Sociale</th>
                  <th className="py-2">Cliente</th>
                  <th className="py-2">Tipo Doc.</th>
                  <th className="py-2 text-end px-3">Valore</th>
                </tr>
              </thead>
              <tbody>
                {righeTabella.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-4">
                      <i className="bi bi-inbox fs-4 d-block mb-1"></i>
                      Nessun articolo stock disponibile.
                    </td>
                  </tr>
                ) : (
                  righeTabella.map((item, idx) => (
                    <tr key={`${item.articolo}-${item.data_ordine}-${idx}`}>
                      <td className="px-3 fw-semibold text-secondary">
                        {formatDataIt(item.giorno)}
                      </td>
                      <td>
                        <Badge
                          bg="dark"
                          className="fw-normal px-2 py-1"
                          style={{ fontSize: "0.7rem" }}
                        >
                          {item.categoria}
                        </Badge>
                      </td>
                      <td className="fw-bold text-dark">{item.articolo}</td>
                      <td>{item.ragione_sociale}</td>
                      <td className="text-muted">
                        {item.codice_cliente}
                        {item.tipo_cliente ? (
                          <small className="ms-1 opacity-75">
                            ({item.tipo_cliente})
                          </small>
                        ) : (
                          ""
                        )}
                      </td>
                      <td>
                        <span className="badge bg-light text-dark border">
                          {item.tipo_doc}
                        </span>
                      </td>
                      <td className="text-end fw-bold px-3 text-dark">
                        {formatToEuro(item.valore)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </div>
      </Modal.Body>

      {/* ── FOOTER ── */}
      <Modal.Footer className="border-0 pt-0 pb-3 px-4">
        <Button variant="outline-secondary" size="sm" onClick={onHide} className="px-4">
          Chiudi
        </Button>
      </Modal.Footer>
    </Modal>  );
}

function CombinedCardExample() {
  const [totalOrders, setTotalOrders] = useState(0);
  const [averageOrders, setAverageOrders] = useState(0);
  const [uniqueClientsCount, setUniqueClientsCount] = useState(0);
  const [origineData, setOrigineData] = useState("");
  const [origineData2, setOrigineData2] = useState("");
  const [valoreOrdini, setValoreOrdini] = useState("");
  const [valoreOrdini2, setValoreOrdini2] = useState("");
  const [error, setError] = useState("");
  const [isError, setIsError] = useState(false);
  const [totalOrdered, setTotalOrdered] = useState(0);
  const [averageTotal, setAverageTotal] = useState(0);
  const [totalLastMinute, setTotalLastMinute] = useState(0);

  // ── Stock: totali per categoria + array grezzo per la modale ──
  const [totalStock, setTotalStock] = useState(0);
  const [totalCrossDocking, setTotalCrossDocking] = useState(0);
  const [totalNoStock, setTotalNoStock] = useState(0);
  const [stockItems, setStockItems] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [showStockModal, setShowStockModal] = useState(false);

  const handleCloseModal = () => setShowModal(false);
  const handleShowModal = (e) => {
    if (e) e.stopPropagation();
    setShowModal(true);
  };

  const handleCloseStockModal = () => setShowStockModal(false);
  const handleShowStockModal = (e) => {
    if (e) e.stopPropagation();
    setShowStockModal(true);
  };

  // 1. Fetch Dati Principali (Ordini, Clienti, Origine)
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get("./dati_dashboard.json");
        const { totale_ordinato, numero_ordini, origine, clienti } =
          res.data.TP;

        if (totale_ordinato && parseFloat(totale_ordinato) > 0) {
          setTotalOrdered(parseFloat(totale_ordinato));
        }

        if (numero_ordini) {
          setTotalOrders(numero_ordini);
        }

        if (origine && origine.length > 0) {
          let totalNumeroMedioWeb = 0;
          let totalNumeroMedioNoweb = 0;

          origine.forEach((item) => {
            if (item.origine === "web") {
              totalNumeroMedioWeb += parseFloat(item.numero_medio);
            } else if (item.origine === "noweb") {
              totalNumeroMedioNoweb += parseFloat(item.numero_medio);
            }
          });

          setAverageOrders(totalNumeroMedioWeb + totalNumeroMedioNoweb);

          const origineWeb = origine.find((item) => item.origine === "web");
          const origineNoWeb = origine.find((item) => item.origine === "noweb");

          if (origineWeb && origineNoWeb) {
            setValoreOrdini(origineWeb.valore);
            setOrigineData(origineWeb.origine);
            setValoreOrdini2(origineNoWeb.valore);
            setOrigineData2(origineNoWeb.origine);
          }
        }

        if (clienti) {
          let uniqueClients = new Set();
          Object.values(clienti).forEach((clienteArray) => {
            clienteArray.forEach((clienteItem) => {
              uniqueClients.add(clienteItem.cliente);
            });
          });
          setUniqueClientsCount(uniqueClients.size);
        }
      } catch (err) {
        console.error(err);
        setIsError(true);
        setError(err.message);
      }
    };
    fetchData();
  }, []);

  // 2. Fetch Media Semestrale (Valore)
  useEffect(() => {
    const fetchAverageData = async () => {
      try {
        const res = await axios.get("./dati_dashboard.json");
        const { origine } = res.data.TP;

        if (origine && origine.length > 0) {
          const webOrigin = origine.find((item) => item.origine === "web");
          const nowebOrigin = origine.find((item) => item.origine === "noweb");

          const webAverage = webOrigin ? parseFloat(webOrigin.valore_medio) : 0;
          const nowebAverage = nowebOrigin
            ? parseFloat(nowebOrigin.valore_medio)
            : 0;

          setAverageTotal(webAverage + nowebAverage);
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchAverageData();
  }, []);

  // 3. Fetch Last Minute + Stock / Cross Docking / No Stock
  useEffect(() => {
    const fetchLastMinuteAndStockData = async () => {
      try {
        const res = await axios.get("./mancata_vendita_dashboard.json");

        // ── LAST MINUTE ──
        const lastMinuti = res.data.TP?.lastminuti || [];
        const today = new Date().toISOString().split("T")[0];

        const filteredLastMinute = lastMinuti.filter((item) => {
          const dataOrdine = item.data_ordine?.split(" ")[0];
          return item.precodice === null && dataOrdine === today;
        });

        const totalLastMinuteValue = filteredLastMinute.reduce(
          (acc, item) => acc + parseFloat(item.ordinato || 0),
          0,
        );

        setTotalLastMinute(totalLastMinuteValue);

        // ── STOCK / CROSS DOCKING / NO STOCK ──
        const stockData = res.data.TP?.stock || [];
        setStockItems(stockData);

        const sumOrdinato = (arr) =>
          arr.reduce((acc, item) => acc + parseFloat(item.ordinato || 0), 0);

        const soloStock = stockData.filter(
          (item) => item.stock === "stock" && !isCrossDocking(item),
        );

        const crossDocking = stockData.filter((item) => isCrossDocking(item));

        const noStock = stockData.filter(
          (item) => item.stock === "no stock" && !isCrossDocking(item),
        );

        setTotalStock(sumOrdinato(soloStock));
        setTotalCrossDocking(sumOrdinato(crossDocking));
        setTotalNoStock(sumOrdinato(noStock));
      } catch (err) {
        console.error("Errore Last Minute / Stock:", err);
      }
    };

    fetchLastMinuteAndStockData();
  }, []);

  const getTrend = (current, average) => {
    if (!average || average === 0) return null;
    const diff = ((current - average) / average) * 100;
    return (
      <span
        className={`fw-bold d-flex align-items-center gap-1 ${diff < 0 ? "text-danger" : "text-success"}`}
        style={{ fontSize: "0.85rem" }}
      >
        {diff.toFixed(2)}%{" "}
        {diff < 0 ? <FaArrowTrendDown /> : <FaArrowTrendUp />}
      </span>
    );
  };

  return (
    <>
      <Card
        className="card ombra ordini-ora card-home hover-effect border-0 tp-summary-card tp-orders-summary-card"
        style={{ borderRadius: "15px", padding: "1.2rem" }}
      >
        <Card.Body
          className="tp-summary-body p-3 tp-summary-light-body"
          style={{ borderRadius: "12px", height: "100%" }}
        >
          {/* Header Card */}
          <div className="d-flex justify-content-between align-items-end mb-3 border-bottom border-secondary pb-2 border-opacity-25">
            <div>
              <p
                className="fw-bold mb-0 text-title text-uppercase tracking-wider"
                style={{ fontSize: "0.75rem", opacity: 0.8 }}
              >
                Info Ordini
              </p>
              <h3 className="text-dark fw-bold mb-0">
                {formatToEuro(totalOrdered)}
              </h3>
            </div>
            <div className="text-end">
              {getTrend(totalOrdered, averageTotal)}
            </div>
          </div>

          {/* Listato Dati */}
          <div className="d-flex flex-column gap-2">
            <div className="d-flex justify-content-between align-items-center py-1">
              <span className="text-dark fw-bold small text-uppercase">
                Media Semestrale
              </span>
              <div className="text-dark fw-bold">
                {formatToEuro(averageTotal)}
              </div>
            </div>

            <div className="d-flex justify-content-between align-items-center py-1">
              <span className="text-secondary fw-bold small text-uppercase">
                Volume Attuale
              </span>
              <div className="text-dark small fw-bold">
                {uniqueClientsCount}{" "}
                <span className="fw-normal text-dark opacity-50">cli.</span> /{" "}
                {totalOrders}{" "}
                <span className="fw-normal text-secondary opacity-50">
                  ord.
                </span>
              </div>
            </div>

            <div className="d-flex justify-content-between align-items-center py-1 border-top border-secondary border-opacity-10">
              <span className="text-secondary fw-bold small text-uppercase">
                Target Ordini
              </span>
              <div className="d-flex align-items-center gap-2 small">
                <span className="text-dark">{averageOrders.toFixed(0)}</span>
                {getTrend(totalOrders, averageOrders)}
              </div>
            </div>

            <div className="d-flex justify-content-between align-items-center py-1 border-top border-secondary border-opacity-10">
              <span className="text-secondary fw-bold small text-uppercase">
                Canali
              </span>
              <div className="text-dark small" style={{ fontSize: "0.8rem" }}>
                <span className="text-danger fw-bold">{origineData}</span>:{" "}
                {formatToEuro(valoreOrdini)} |{" "}
                <span className="text-secondary uppercase fw-bold">
                  {origineData2}
                </span>
                : {formatToEuro(valoreOrdini2)}
              </div>
            </div>

            {/* Box Last Minute */}
            <div
              className="mt-2 p-2 rounded tp-kpi-box tp-kpi-last-minute"
              style={{
                backgroundColor: "#fff5f5",
                border: "1px solid #f5c2c7",
                cursor: "pointer",
                transition: "box-shadow 0.15s ease, transform 0.15s ease",
              }}
              onClick={handleShowModal}
              role="button"
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow =
                  "0 4px 12px rgba(220,53,69,0.18)";
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span
                  className="text-danger fw-bold text-uppercase"
                  style={{ fontSize: "0.65rem" }}
                >
                  Last Minute
                </span>

                <span className="text-dark small fw-bold">
                  {formatToEuro(totalLastMinute)}
                </span>
              </div>
            </div>

            {/* Box Stock / Cross Docking / No Stock */}
            <div
              className="mt-2 p-2 rounded tp-kpi-box tp-kpi-stock"
              style={{
                backgroundColor: "#f8f9fa",
                border: "1px solid #e9ecef",
                cursor: "pointer",
                transition: "box-shadow 0.15s ease, transform 0.15s ease",
              }}
              onClick={handleShowStockModal}
              role="button"
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.12)";
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span
                  className="text-dark fw-bold text-uppercase"
                  style={{ fontSize: "0.65rem" }}
                >
                  Stock
                </span>
                <span className="text-dark small fw-bold">
                  {formatToEuro(totalStock)}
                </span>
              </div>

              <div className="d-flex justify-content-between align-items-center mt-1">
                <span
                  className="text-primary fw-bold text-uppercase"
                  style={{ fontSize: "0.65rem" }}
                >
                  Cross Docking
                </span>
                <span className="text-dark small fw-bold">
                  {formatToEuro(totalCrossDocking)}
                </span>
              </div>

              <div className="d-flex justify-content-between align-items-center mt-1">
                <span
                  className="text-secondary fw-bold text-uppercase"
                  style={{ fontSize: "0.65rem" }}
                >
                  No Stock
                </span>
                <span className="text-dark small fw-bold">
                  {formatToEuro(totalNoStock)}
                </span>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>


      <style>{`
        /* =====================================================
           TECHNOPARTS LIGHT — INFO ORDINI
           Solo la card principale.
           I modal restano indipendenti.
           ===================================================== */

        .tp-orders-summary-card {
          color: #252a31 !important;
        }

        .tp-orders-summary-card .tp-summary-light-body {
          color: #252a31 !important;
          background: transparent !important;
        }

        /* Titolo */
        .tp-orders-summary-card .text-title {
          color: #b81f25 !important;
          opacity: 1 !important;
        }

        /* Testi principali */
        .tp-orders-summary-card .text-dark {
          color: #252a31 !important;
        }

        /* Secondari */
        .tp-orders-summary-card .text-secondary,
        .tp-orders-summary-card .text-muted {
          color: #6f7680 !important;
        }

        /* Forza i testi generici ad essere leggibili sul glass chiaro */
        .tp-orders-summary-card p,
        .tp-orders-summary-card small,
        .tp-orders-summary-card span,
        .tp-orders-summary-card div {
          text-shadow: none !important;
        }

        /* Non sovrascrivere i colori semantici */
        .tp-orders-summary-card .text-danger {
          color: #b81f25 !important;
        }

        .tp-orders-summary-card .text-success {
          color: #198754 !important;
        }

        .tp-orders-summary-card .text-primary {
          color: #0d6efd !important;
        }

        /* Divider più adatto al tema chiaro */
        .tp-orders-summary-card .border-secondary {
          border-color: rgba(31, 38, 48, 0.10) !important;
        }

        /* -----------------------------------------------------
           LAST MINUTE
           ----------------------------------------------------- */

        .tp-orders-summary-card .tp-kpi-last-minute {
          background:
            linear-gradient(
              145deg,
              rgba(184,31,37,0.075),
              rgba(255,255,255,0.48)
            ) !important;

          border:
            1px solid rgba(184,31,37,0.14) !important;

          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.72);
        }

        .tp-orders-summary-card
        .tp-kpi-last-minute
        .text-danger {
          color: #b81f25 !important;
        }

        .tp-orders-summary-card
        .tp-kpi-last-minute
        .text-dark {
          color: #252a31 !important;
        }

        /* -----------------------------------------------------
           STOCK / CROSS DOCKING / NO STOCK
           ----------------------------------------------------- */

        .tp-orders-summary-card .tp-kpi-stock {
          background:
            rgba(255,255,255,0.52) !important;

          border:
            1px solid rgba(31,38,48,0.09) !important;

          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.72);
        }

        .tp-orders-summary-card
        .tp-kpi-stock
        .text-dark {
          color: #252a31 !important;
        }

        .tp-orders-summary-card
        .tp-kpi-stock
        .text-secondary {
          color: #747b85 !important;
        }

        /* Cross Docking resta blu perché è un'informazione distinta */
        .tp-orders-summary-card
        .tp-kpi-stock
        .text-primary {
          color: #0d6efd !important;
        }

        /* -----------------------------------------------------
           CANALI
           ----------------------------------------------------- */

        .tp-orders-summary-card .text-danger.fw-bold {
          color: #b81f25 !important;
        }

        .tp-orders-summary-card
        .uppercase {
          text-transform: uppercase;
        }

        /* -----------------------------------------------------
           TREND
           ----------------------------------------------------- */

        .tp-orders-summary-card .text-success {
          color: #198754 !important;
        }

        .tp-orders-summary-card .text-danger {
          color: #dc3545 !important;
        }

        /* Però il canale WEB resta rosso Technoparts */
        .tp-orders-summary-card
        .text-danger.fw-bold:not(.d-flex) {
          color: #b81f25 !important;
        }

        /* -----------------------------------------------------
           MOBILE
           ----------------------------------------------------- */

        @media screen and (max-width: 767px) {
          .tp-orders-summary-card .tp-summary-light-body {
            padding: 0.9rem !important;
          }

          .tp-orders-summary-card h3 {
            font-size: 1.35rem !important;
          }

          .tp-orders-summary-card
          .tp-kpi-last-minute,
          .tp-orders-summary-card
          .tp-kpi-stock {
            background:
              rgba(255,255,255,0.58) !important;
          }
        }
      `}</style>

      <Dettagli showModal={showModal} handleCloseModal={handleCloseModal} />

      <StockModal
        show={showStockModal}
        onHide={handleCloseStockModal}
        stockItems={stockItems}
      />
    </>
  );
}

export default CombinedCardExample;